'use client';

import React, { useState, useCallback, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { AIRPORTS, FlightOffer, FlightSearchParams, Airport, flightProvider,  } from '@/lib/flights/demoData';

// ─── Types ────────────────────────────────────────────────────────────────────
type TripType = 'one_way' | 'round_trip' | 'multi_city';
type CabinClass = 'economy' | 'premium_economy' | 'business' | 'first';
type SortOption = 'recommended' | 'cheapest' | 'fastest' | 'earliest_dep' | 'earliest_arr';

interface FilterState {
  directOnly: boolean;
  maxPrice: number;
  airlines: string[];
  depTimeRange: [number, number]; // hours 0-24
  arrTimeRange: [number, number];
  baggageIncluded: boolean;
  refundableOnly: boolean;
  maxStops: number;
}

const CABIN_LABELS: Record<CabinClass, string> = {
  economy: 'Economy',
  premium_economy: 'Premium Economy',
  business: 'Business',
  first: 'First Class',
};

const SORT_LABELS: Record<SortOption, string> = {
  recommended: 'Recommended',
  cheapest: 'Cheapest',
  fastest: 'Fastest',
  earliest_dep: 'Earliest Departure',
  earliest_arr: 'Earliest Arrival',
};

// ─── Timezone-aware time formatting ──────────────────────────────────────────
function formatTime(isoString: string): string {
  const d = new Date(isoString);
  const h = d.getUTCHours().toString().padStart(2, '0');
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

function formatTimeLocal(isoString: string, timezone: string): string {
  // Use Intl to format in the destination timezone
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    }).format(new Date(isoString));
  } catch {
    return formatTime(isoString);
  }
}

function formatDateLocal(isoString: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: timezone,
    }).format(new Date(isoString));
  } catch {
    const d = new Date(isoString);
    return d.toDateString();
  }
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatNPR(n: number): string {
  return `NPR ${n.toLocaleString('en-NP')}`;
}

// Check if arrival is next day relative to departure (in their respective timezones)
function getNextDayIndicator(depISO: string, arrISO: string, depTZ: string, arrTZ: string): string {
  try {
    const depDate = new Intl.DateTimeFormat('en-CA', { timeZone: depTZ }).format(new Date(depISO));
    const arrDate = new Intl.DateTimeFormat('en-CA', { timeZone: arrTZ }).format(new Date(arrISO));
    const depMs = new Date(depDate).getTime();
    const arrMs = new Date(arrDate).getTime();
    const diffDays = Math.round((arrMs - depMs) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return '+1 day';
    if (diffDays === 2) return '+2 days';
    if (diffDays > 2) return `+${diffDays} days`;
    return '';
  } catch {
    return '';
  }
}

// ─── Airport Search Dropdown ──────────────────────────────────────────────────
interface AirportPickerProps {
  label: string;
  value: string;
  onChange: (iata: string) => void;
  excludeIata?: string;
  placeholder?: string;
}

function AirportPicker({ label, value, onChange, excludeIata, placeholder }: AirportPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = AIRPORTS[value];

  const filtered = Object.values(AIRPORTS).filter(a => {
    if (a.iata === excludeIata) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.iata.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q) ||
      a.city.toLowerCase().includes(q) ||
      a.country.toLowerCase().includes(q)
    );
  }).slice(0, 8);

  return (
    <div className="relative">
      <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--gold)' }}>{label}</label>
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="w-full text-left px-4 py-3 rounded-xl transition-colors"
        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: selected ? 'var(--himal-white)' : 'rgba(245,240,232,0.4)' }}
      >
        {selected ? (
          <div>
            <div className="font-bold text-base" style={{ color: 'var(--glacier-light)' }}>{selected.iata}</div>
            <div className="text-xs truncate" style={{ color: 'rgba(245,240,232,0.7)' }}>{selected.city}, {selected.country}</div>
          </div>
        ) : (
          <span className="text-sm">{placeholder || 'Select airport'}</span>
        )}
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden z-50 shadow-2xl" style={{ background: '#0F2A5A', border: '1px solid rgba(245,240,232,0.15)' }}>
          <div className="p-2">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search city, airport, IATA code…"
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--himal-white)' }}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>No airports found</div>
            ) : filtered.map(a => (
              <button
                key={a.iata}
                type="button"
                onClick={() => { onChange(a.iata); setOpen(false); setSearch(''); }}
                className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex items-start gap-3"
              >
                <span className="text-base font-bold mt-0.5 w-10 shrink-0" style={{ color: 'var(--glacier-light)' }}>{a.iata}</span>
                <div>
                  <div className="text-sm font-medium" style={{ color: 'var(--himal-white)' }}>{a.city}</div>
                  <div className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>{a.name}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Flight Result Card ───────────────────────────────────────────────────────
interface FlightCardProps {
  offer: FlightOffer;
  onSelect: (offer: FlightOffer) => void;
}

function FlightCard({ offer, onSelect }: FlightCardProps) {
  const seg = offer.segments[0];
  const depTime = formatTimeLocal(seg.departureISO, seg.departureAirport.timezone);
  const arrTime = formatTimeLocal(seg.arrivalISO, seg.arrivalAirport.timezone);
  const depDate = formatDateLocal(seg.departureISO, seg.departureAirport.timezone);
  const arrDate = formatDateLocal(seg.arrivalISO, seg.arrivalAirport.timezone);
  const nextDay = getNextDayIndicator(seg.departureISO, seg.arrivalISO, seg.departureAirport.timezone, seg.arrivalAirport.timezone);

  return (
    <div className="rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl" style={{ background: 'rgba(27,58,107,0.72)', border: '1px solid rgba(245,240,232,0.1)' }}>
      {/* Demo banner */}
      <div className="px-4 py-1.5 flex items-center gap-2" style={{ background: 'rgba(245,192,48,0.08)', borderBottom: '1px solid rgba(245,192,48,0.15)' }}>
        <span className="text-[10px]" style={{ color: 'rgba(245,192,48,0.7)' }}>⚠️ {offer.demoLabel}</span>
      </div>

      <div className="p-4 md:p-5">
        {/* Airline row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
              {seg.airlineLogo ? (
                <img src={seg.airlineLogo} alt={`${seg.airline} logo`} className="w-8 h-8 object-contain" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <span className="text-lg">✈️</span>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color: 'var(--himal-white)' }}>{seg.airline}</div>
              <div className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>{seg.flightNumber} · {seg.aircraft || 'Aircraft TBD'}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(26,188,212,0.12)', border: '1px solid rgba(26,188,212,0.2)', color: 'var(--glacier-light)' }}>
              {CABIN_LABELS[seg.cabinClass]}
            </div>
          </div>
        </div>

        {/* Route row */}
        <div className="flex items-center gap-3 mb-4">
          {/* Departure */}
          <div className="flex-1 min-w-0">
            <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--himal-white)' }}>{depTime}</div>
            <div className="text-xs font-semibold mt-0.5" style={{ color: 'var(--glacier-light)' }}>{seg.departureAirport.iata}</div>
            <div className="text-xs truncate" style={{ color: 'rgba(245,240,232,0.55)' }}>{seg.departureAirport.city}</div>
            <div className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>{depDate}</div>
          </div>

          {/* Duration / stops */}
          <div className="flex flex-col items-center flex-shrink-0 px-2">
            <div className="text-xs font-medium mb-1" style={{ color: 'rgba(245,240,232,0.5)' }}>{formatDuration(seg.durationMinutes)}</div>
            <div className="flex items-center gap-1 w-24 md:w-32">
              <div className="h-px flex-1" style={{ background: 'rgba(26,188,212,0.4)' }} />
              {seg.stops.length === 0 ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(33,181,115,0.15)', color: '#21B573', border: '1px solid rgba(33,181,115,0.25)' }}>Direct</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(245,116,66,0.15)', color: '#F57442', border: '1px solid rgba(245,116,66,0.25)' }}>
                  {seg.stops.length} stop{seg.stops.length > 1 ? 's' : ''}
                </span>
              )}
              <div className="h-px flex-1" style={{ background: 'rgba(26,188,212,0.4)' }} />
            </div>
            {seg.stops.length > 0 && (
              <div className="text-[10px] mt-1 text-center" style={{ color: 'rgba(245,240,232,0.4)' }}>
                via {seg.stops.map(s => s.airport.iata).join(', ')}
              </div>
            )}
          </div>

          {/* Arrival */}
          <div className="flex-1 min-w-0 text-right">
            <div className="flex items-start justify-end gap-1">
              <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--himal-white)' }}>{arrTime}</div>
              {nextDay && (
                <span className="text-[10px] font-semibold mt-1 px-1.5 py-0.5 rounded" style={{ background: 'rgba(245,116,66,0.2)', color: '#F57442' }}>{nextDay}</span>
              )}
            </div>
            <div className="text-xs font-semibold mt-0.5" style={{ color: 'var(--glacier-light)' }}>{seg.arrivalAirport.iata}</div>
            <div className="text-xs truncate" style={{ color: 'rgba(245,240,232,0.55)' }}>{seg.arrivalAirport.city}</div>
            <div className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>{arrDate}</div>
          </div>
        </div>

        {/* Baggage + fare info */}
        <div className="flex flex-wrap items-center gap-2 mb-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <span className="text-[10px] px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.6)' }}>
            🧳 {offer.baggage.checkedKg}kg checked
          </span>
          <span className="text-[10px] px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.6)' }}>
            💼 {offer.baggage.cabinKg}kg cabin
          </span>
          <span className="text-[10px] px-2 py-1 rounded-lg" style={{ background: offer.fare.refundable ? 'rgba(33,181,115,0.1)' : 'rgba(245,116,66,0.1)', color: offer.fare.refundable ? '#21B573' : '#F57442', border: `1px solid ${offer.fare.refundable ? 'rgba(33,181,115,0.2)' : 'rgba(245,116,66,0.2)'}` }}>
            {offer.fare.refundable ? '✓ Refundable' : '✗ Non-refundable'}
          </span>
          {offer.seatsAvailable && offer.seatsAvailable <= 5 && (
            <span className="text-[10px] px-2 py-1 rounded-lg" style={{ background: 'rgba(245,116,66,0.1)', color: '#F57442', border: '1px solid rgba(245,116,66,0.2)' }}>
              Only {offer.seatsAvailable} seats left
            </span>
          )}
        </div>

        {/* Price + CTA */}
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-xs mb-0.5" style={{ color: 'rgba(245,240,232,0.5)' }}>per person</div>
            <div className="text-xl font-bold" style={{ color: 'var(--gold-bright)' }}>{formatNPR(offer.fare.total)}</div>
            <div className="text-[10px]" style={{ color: 'rgba(245,240,232,0.4)' }}>
              Base {formatNPR(offer.fare.baseFare)} + Tax {formatNPR(offer.fare.taxes)}
            </div>
          </div>
          <Link
            href={`/flights/${offer.id}`}
            className="px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, var(--glacier), var(--glacier-dim))', color: '#071A38' }}
            onClick={() => onSelect(offer)}
          >
            Select Flight
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton Card ────────────────────────────────────────────────────────────
function FlightSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(27,58,107,0.5)', border: '1px solid rgba(245,240,232,0.07)' }}>
      <div className="h-8 animate-pulse" style={{ background: 'rgba(245,192,48,0.06)' }} />
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl animate-pulse" style={{ background: 'rgba(35,74,133,0.5)' }} />
          <div className="space-y-1.5 flex-1">
            <div className="h-4 rounded w-1/3 animate-pulse" style={{ background: 'rgba(35,74,133,0.5)' }} />
            <div className="h-3 rounded w-1/4 animate-pulse" style={{ background: 'rgba(35,74,133,0.4)' }} />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1 space-y-1">
            <div className="h-8 rounded w-20 animate-pulse" style={{ background: 'rgba(35,74,133,0.5)' }} />
            <div className="h-3 rounded w-12 animate-pulse" style={{ background: 'rgba(35,74,133,0.4)' }} />
          </div>
          <div className="w-24 h-6 rounded animate-pulse" style={{ background: 'rgba(35,74,133,0.4)' }} />
          <div className="flex-1 space-y-1 items-end flex flex-col">
            <div className="h-8 rounded w-20 animate-pulse" style={{ background: 'rgba(35,74,133,0.5)' }} />
            <div className="h-3 rounded w-12 animate-pulse" style={{ background: 'rgba(35,74,133,0.4)' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Flights Page ────────────────────────────────────────────────────────
export default function FlightsPage() {
  const [tripType, setTripType] = useState<TripType>('one_way');
  const [origin, setOrigin] = useState('KTM');
  const [destination, setDestination] = useState('DEL');
  const [departureDate, setDepartureDate] = useState('2026-08-04');
  const [returnDate, setReturnDate] = useState('2026-08-11');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [cabinClass, setCabinClass] = useState<CabinClass>('economy');
  const [directOnly, setDirectOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('recommended');
  const [results, setResults] = useState<FlightOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    directOnly: false,
    maxPrice: 200000,
    airlines: [],
    depTimeRange: [0, 24],
    arrTimeRange: [0, 24],
    baggageIncluded: false,
    refundableOnly: false,
    maxStops: 3,
  });

  const swapAirports = () => {
    if (origin === destination) return;
    const tmp = origin;
    setOrigin(destination);
    setDestination(tmp);
  };

  const handleSearch = useCallback(async () => {
    if (!origin || !destination) return;
    if (origin === destination) {
      alert('Origin and destination cannot be the same.');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const params: FlightSearchParams = {
        origin,
        destination,
        departureDate,
        returnDate: tripType === 'round_trip' ? returnDate : undefined,
        tripType,
        adults,
        children,
        infants,
        cabinClass,
        directOnly,
      };
      const offers = await flightProvider.searchFlights(params);
      setResults(offers);
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, [origin, destination, departureDate, returnDate, tripType, adults, children, infants, cabinClass, directOnly]);

  // Apply filters and sorting
  const filteredResults = results.filter(offer => {
    const seg = offer.segments[0];
    if (filters.directOnly && seg.stops.length > 0) return false;
    if (offer.fare.total > filters.maxPrice) return false;
    if (filters.airlines.length > 0 && !filters.airlines.includes(seg.airlineCode)) return false;
    if (filters.refundableOnly && !offer.fare.refundable) return false;
    if (filters.baggageIncluded && offer.baggage.checkedKg < 15) return false;
    if (seg.stops.length > filters.maxStops) return false;
    return true;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    const sa = a.segments[0];
    const sb = b.segments[0];
    if (sortBy === 'cheapest') return a.fare.total - b.fare.total;
    if (sortBy === 'fastest') return sa.durationMinutes - sb.durationMinutes;
    if (sortBy === 'earliest_dep') return new Date(sa.departureISO).getTime() - new Date(sb.departureISO).getTime();
    if (sortBy === 'earliest_arr') return new Date(sa.arrivalISO).getTime() - new Date(sb.arrivalISO).getTime();
    // recommended: balance price + duration
    return (a.fare.total * 0.6 + sa.durationMinutes * 10) - (b.fare.total * 0.6 + sb.durationMinutes * 10);
  });

  const availableAirlines = [...new Set(results.map(o => o.segments[0].airlineCode))];
  const minPrice = results.length > 0 ? Math.min(...results.map(o => o.fare.total)) : 0;
  const maxPrice = results.length > 0 ? Math.max(...results.map(o => o.fare.total)) : 200000;

  return (
    <div className="min-h-screen" style={{ background: 'var(--royal-blue)' }}>
      <Header />

      <div className="pt-20">
        {/* Hero / Search Form */}
        <div className="py-8 px-4" style={{ background: 'linear-gradient(180deg, #0F2A5A 0%, #1B3A6B 100%)' }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--glacier-light)' }}>✈️ Flight Search</div>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold mb-2" style={{ color: 'var(--himal-white)' }}>
              Find <span className="italic" style={{ color: 'var(--gold-bright)' }}>Cheap Flights</span>
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(245,240,232,0.55)' }}>
              Search flights from Nepal to anywhere in the world
            </p>

            {/* Demo notice */}
            <div className="mb-5 px-4 py-2.5 rounded-xl flex items-start gap-2 text-xs" style={{ background: 'rgba(245,192,48,0.08)', border: '1px solid rgba(245,192,48,0.2)', color: 'rgba(245,192,48,0.8)' }}>
              <span className="shrink-0 mt-0.5">⚠️</span>
              <span>
                <strong>Demo Mode:</strong> No real flight provider is connected. All fares are sample data for UI testing only.
                Real booking is disabled. Connect a provider (Amadeus, Sabre, Travelport) to enable live search and ticketing.
              </span>
            </div>

            {/* Search form */}
            <div className="rounded-2xl p-5 md:p-6" style={{ background: 'rgba(27,58,107,0.72)', border: '1px solid rgba(245,240,232,0.1)', backdropFilter: 'blur(20px)' }}>
              {/* Trip type */}
              <div className="flex gap-2 mb-5 flex-wrap">
                {(['one_way', 'round_trip', 'multi_city'] as TripType[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTripType(t)}
                    className="px-4 py-2 rounded-full text-xs font-medium transition-all"
                    style={{
                      background: tripType === t ? 'linear-gradient(135deg, var(--glacier), var(--glacier-dim))' : 'rgba(255,255,255,0.07)',
                      border: tripType === t ? '1px solid rgba(26,188,212,0.4)' : '1px solid rgba(255,255,255,0.1)',
                      color: tripType === t ? '#071A38' : 'rgba(245,240,232,0.75)',
                      fontWeight: tripType === t ? '600' : '500',
                    }}
                  >
                    {t === 'one_way' ? 'One Way' : t === 'round_trip' ? 'Round Trip' : 'Multi-city'}
                  </button>
                ))}
              </div>

              {/* Origin / Destination */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 mb-4 items-end">
                <AirportPicker
                  label="From"
                  value={origin}
                  onChange={setOrigin}
                  excludeIata={destination}
                  placeholder="Select departure city"
                />
                <button
                  type="button"
                  onClick={swapAirports}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 self-end mb-0.5"
                  style={{ background: 'rgba(26,188,212,0.15)', border: '1px solid rgba(26,188,212,0.3)', color: 'var(--glacier-light)' }}
                  title="Swap airports"
                  aria-label="Swap origin and destination"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 16V4m0 0L3 8m4-4 4 4M17 8v12m0 0 4-4m-4 4-4-4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <AirportPicker
                  label="To"
                  value={destination}
                  onChange={setDestination}
                  excludeIata={origin}
                  placeholder="Select destination city"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--gold)' }}>Departure Date</label>
                  <input
                    type="date"
                    value={departureDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={e => setDepartureDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--himal-white)', colorScheme: 'dark' }}
                  />
                </div>
                {tripType === 'round_trip' && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--gold)' }}>Return Date</label>
                    <input
                      type="date"
                      value={returnDate}
                      min={departureDate}
                      onChange={e => setReturnDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--himal-white)', colorScheme: 'dark' }}
                    />
                  </div>
                )}
              </div>

              {/* Passengers + Cabin */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Adults', value: adults, setter: setAdults, min: 1, max: 9 },
                  { label: 'Children (2-11)', value: children, setter: setChildren, min: 0, max: 8 },
                  { label: 'Infants (<2)', value: infants, setter: setInfants, min: 0, max: adults },
                ].map(({ label, value, setter, min, max }) => (
                  <div key={label}>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--gold)' }}>{label}</label>
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                      <button type="button" onClick={() => setter(Math.max(min, value - 1))} className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold hover:bg-white/10 transition-colors" style={{ color: 'var(--glacier-light)' }}>−</button>
                      <span className="flex-1 text-center text-sm font-semibold" style={{ color: 'var(--himal-white)' }}>{value}</span>
                      <button type="button" onClick={() => setter(Math.min(max, value + 1))} className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold hover:bg-white/10 transition-colors" style={{ color: 'var(--glacier-light)' }}>+</button>
                    </div>
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--gold)' }}>Cabin Class</label>
                  <select
                    value={cabinClass}
                    onChange={e => setCabinClass(e.target.value as CabinClass)}
                    className="w-full px-3 py-3 rounded-xl text-sm focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--himal-white)' }}
                  >
                    {Object.entries(CABIN_LABELS).map(([k, v]) => (
                      <option key={k} value={k} style={{ background: '#1B3A6B' }}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Direct flights toggle + Search button */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={() => setDirectOnly(!directOnly)}
                    className="relative w-10 h-5 rounded-full transition-colors cursor-pointer"
                    style={{ background: directOnly ? 'var(--glacier)' : 'rgba(255,255,255,0.15)' }}
                  >
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: directOnly ? 'translateX(20px)' : 'translateX(0)' }} />
                  </div>
                  <span className="text-sm" style={{ color: 'rgba(245,240,232,0.8)' }}>Direct flights only</span>
                </label>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={!origin || !destination || origin === destination}
                  className="w-full sm:w-auto px-8 py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: '#071A38', minWidth: '160px' }}
                >
                  {loading ? (
                    <span className="flex items-center gap-2 justify-center">
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/></svg>
                      Searching…
                    </span>
                  ) : '🔍 Search Flights'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results section */}
        {searched && (
          <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Filters sidebar */}
              <div className={`lg:w-72 shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                <div className="rounded-2xl p-5 sticky top-24" style={{ background: 'rgba(27,58,107,0.72)', border: '1px solid rgba(245,240,232,0.1)', backdropFilter: 'blur(20px)' }}>
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--himal-white)' }}>Filters</h3>
                    <button
                      onClick={() => setFilters({ directOnly: false, maxPrice: maxPrice, airlines: [], depTimeRange: [0, 24], arrTimeRange: [0, 24], baggageIncluded: false, refundableOnly: false, maxStops: 3 })}
                      className="text-xs"
                      style={{ color: 'var(--glacier)' }}
                    >
                      Reset
                    </button>
                  </div>

                  {/* Direct only */}
                  <div className="mb-5">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => setFilters(f => ({ ...f, directOnly: !f.directOnly }))}
                        className="relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0"
                        style={{ background: filters.directOnly ? 'var(--glacier)' : 'rgba(255,255,255,0.15)' }}
                      >
                        <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: filters.directOnly ? 'translateX(20px)' : 'translateX(0)' }} />
                      </div>
                      <span className="text-sm" style={{ color: 'rgba(245,240,232,0.8)' }}>Direct flights only</span>
                    </label>
                  </div>

                  {/* Refundable */}
                  <div className="mb-5">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => setFilters(f => ({ ...f, refundableOnly: !f.refundableOnly }))}
                        className="relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0"
                        style={{ background: filters.refundableOnly ? 'var(--glacier)' : 'rgba(255,255,255,0.15)' }}
                      >
                        <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: filters.refundableOnly ? 'translateX(20px)' : 'translateX(0)' }} />
                      </div>
                      <span className="text-sm" style={{ color: 'rgba(245,240,232,0.8)' }}>Refundable fares only</span>
                    </label>
                  </div>

                  {/* Baggage included */}
                  <div className="mb-5">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => setFilters(f => ({ ...f, baggageIncluded: !f.baggageIncluded }))}
                        className="relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0"
                        style={{ background: filters.baggageIncluded ? 'var(--glacier)' : 'rgba(255,255,255,0.15)' }}
                      >
                        <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: filters.baggageIncluded ? 'translateX(20px)' : 'translateX(0)' }} />
                      </div>
                      <span className="text-sm" style={{ color: 'rgba(245,240,232,0.8)' }}>Baggage included (15kg+)</span>
                    </label>
                  </div>

                  {/* Max stops */}
                  <div className="mb-5">
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--gold)' }}>Max Stops</label>
                    <div className="flex gap-2">
                      {[0, 1, 2, 3].map(n => (
                        <button
                          key={n}
                          onClick={() => setFilters(f => ({ ...f, maxStops: n }))}
                          className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
                          style={{ background: filters.maxStops === n ? 'rgba(26,188,212,0.2)' : 'rgba(255,255,255,0.05)', border: filters.maxStops === n ? '1px solid rgba(26,188,212,0.4)' : '1px solid rgba(255,255,255,0.08)', color: filters.maxStops === n ? 'var(--glacier-light)' : 'rgba(245,240,232,0.6)' }}
                        >
                          {n === 0 ? 'Direct' : n === 3 ? 'Any' : `${n}+`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Airlines */}
                  {availableAirlines.length > 0 && (
                    <div className="mb-5">
                      <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--gold)' }}>Airlines</label>
                      <div className="space-y-1.5">
                        {availableAirlines.map(code => {
                          const offer = results.find(o => o.segments[0].airlineCode === code);
                          const name = offer?.segments[0].airline || code;
                          const checked = filters.airlines.includes(code);
                          return (
                            <label key={code} className="flex items-center gap-2.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setFilters(f => ({ ...f, airlines: checked ? f.airlines.filter(a => a !== code) : [...f.airlines, code] }))}
                                className="w-4 h-4 rounded"
                                style={{ accentColor: 'var(--glacier)' }}
                              />
                              <span className="text-sm" style={{ color: 'rgba(245,240,232,0.8)' }}>{name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Results */}
              <div className="flex-1 min-w-0">
                {/* Results header */}
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.8)' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
                    Filters
                  </button>

                  {!loading && (
                    <span className="text-sm" style={{ color: 'rgba(245,240,232,0.6)' }}>
                      {sortedResults.length} flight{sortedResults.length !== 1 ? 's' : ''} found
                    </span>
                  )}

                  {/* Sort */}
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-xs hidden sm:block" style={{ color: 'rgba(245,240,232,0.5)' }}>Sort:</span>
                    <div className="flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                      {(Object.keys(SORT_LABELS) as SortOption[]).map(s => (
                        <button
                          key={s}
                          onClick={() => setSortBy(s)}
                          className="px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap"
                          style={{
                            flexShrink: 0,
                            background: sortBy === s ? 'rgba(26,188,212,0.2)' : 'rgba(255,255,255,0.05)',
                            border: sortBy === s ? '1px solid rgba(26,188,212,0.4)' : '1px solid rgba(255,255,255,0.08)',
                            color: sortBy === s ? 'var(--glacier-light)' : 'rgba(245,240,232,0.6)',
                          }}
                        >
                          {SORT_LABELS[s]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Loading */}
                {loading && (
                  <div className="space-y-4">
                    {[1,2,3].map(i => <FlightSkeleton key={i} />)}
                  </div>
                )}

                {/* Empty */}
                {!loading && sortedResults.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="text-5xl mb-4">✈️</div>
                    <div className="text-lg font-semibold mb-2" style={{ color: 'var(--himal-white)' }}>No flights found</div>
                    <p className="text-sm mb-4" style={{ color: 'rgba(245,240,232,0.5)' }}>
                      Try different dates, airports, or remove filters
                    </p>
                    <button
                      onClick={() => setFilters(f => ({ ...f, directOnly: false, refundableOnly: false, baggageIncluded: false, maxStops: 3, airlines: [] }))}
                      className="text-xs px-4 py-2 rounded-xl"
                      style={{ background: 'rgba(26,188,212,0.1)', border: '1px solid rgba(26,188,212,0.2)', color: 'var(--glacier-light)' }}
                    >
                      Clear filters
                    </button>
                  </div>
                )}

                {/* Results */}
                {!loading && sortedResults.length > 0 && (
                  <div className="space-y-4">
                    {sortedResults.map(offer => (
                      <FlightCard key={offer.id} offer={offer} onSelect={() => {}} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Initial state — popular routes */}
        {!searched && (
          <div className="max-w-5xl mx-auto px-4 py-10 pb-24">
            <div className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'var(--glacier-light)' }}>Popular Routes from Nepal</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { from: 'KTM', to: 'DEL', label: 'Kathmandu → Delhi', price: 'From NPR 12,500', icon: '🇮🇳' },
                { from: 'KTM', to: 'DXB', label: 'Kathmandu → Dubai', price: 'From NPR 34,300', icon: '🇦🇪' },
                { from: 'KTM', to: 'BKK', label: 'Kathmandu → Bangkok', price: 'From NPR 27,600', icon: '🇹🇭' },
                { from: 'KTM', to: 'SIN', label: 'Kathmandu → Singapore', price: 'From NPR 39,300', icon: '🇸🇬' },
                { from: 'KTM', to: 'LHR', label: 'Kathmandu → London', price: 'From NPR 94,200', icon: '🇬🇧' },
                { from: 'KTM', to: 'BOM', label: 'Kathmandu → Mumbai', price: 'From NPR 15,000', icon: '🇮🇳' },
              ].map(route => (
                <button
                  key={`${route.from}-${route.to}`}
                  onClick={() => { setOrigin(route.from); setDestination(route.to); }}
                  className="flex items-center gap-4 p-4 rounded-2xl text-left transition-all hover:scale-[1.02]"
                  style={{ background: 'rgba(26,188,212,0.06)', border: '1px solid rgba(26,188,212,0.15)' }}
                >
                  <div className="text-2xl">{route.icon}</div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--himal-white)' }}>{route.label}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--glacier-light)' }}>{route.price}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
